// 引入 React 与常用 Hooks。
import React, { useMemo, useContext, useEffect } from 'react';
// 引入国际化能力。
import { useTranslations } from 'next-intl';
// 引入通用图片组件。
import CommonImage, { ImageType } from '@/components/CommonImage';
// 引入样式模块。
import styles from './index.module.scss';
// 引入页面路径和 Lottie 地址常量。
import { pagePathUrl, USER_ACTION_LOTTIE_URL } from '@/constants/url';
// 引入帖子列表项类型。
import { IThreadLisItem } from '@/api/thread/list';
// 引入数字格式化工具。
import { formatDisplayNumber } from '@/utils/formatNumber';
// 引入用户互动组件与类型。
import UserAction, { USER_ACTION_TYPE } from '@/components/UserAction';
// 引入用户互动计数组件。
import UserActionCount from '@/components/UserActionCount/mIndex';
// 引入全局上下文与命名空间。
import { StoreContext, namespace } from '@/context';
// 引入异步状态枚举。
import { PromiseState } from '@/constants';
// 引入 SLS 上报 Hook 与类型。
import useSls, { SlsData } from '@/hooks/useSls';
// 引入 Lottie 资源预热函数。
import { preloadDotLottieAssets } from '@/components/DotLottie';
// 引入 HTML 实体解码工具。
import { decodeHtmlEntities } from '@/utils/html';

// 引入评论图标。
import CommentSvg from '../../../../assets/icon/feed/comment.svg';
// 引入草稿编辑图标。
import GoToEditIcon from '../../../../assets/icon/account/goto_edit.svg';
// 引入骨架帖子项组件。
import { SkeletonPostItem } from '../SkeletonUserPostList';
// 引入 HTML 清洗库。
import sanitizeHtml from 'sanitize-html';

// 定义组件 Props。
interface UserPostListProps {
  // 列表数据。
  data: IThreadLisItem[];
  // 是否草稿列表。
  isDraft?: boolean;
  // 是否加载中。
  loading?: boolean;
  // 项目更新回调。
  onItemUpdate?: (item: IThreadLisItem, type: USER_ACTION_TYPE, newStatus: boolean) => void;
  // 页面名（埋点字段）。
  pageName?: 'MyProfilePage' | 'UserProfilePage';
  // 列表名（埋点字段）。
  listName?: string;
}

// 用户帖子列表组件。
const UserPostList: React.FC<UserPostListProps> = ({
  // 帖子数据。
  data,
  // 默认非草稿。
  isDraft = false,
  // 默认非 loading。
  loading = false,
  // 父层更新回调。
  onItemUpdate,
  // 默认页名。
  pageName = 'MyProfilePage',
  // 列表名可选。
  listName,
}) => {
  // 国际化函数。
  const t = useTranslations();
  // 读取全局状态。
  const { state } = useContext(StoreContext);
  // 取 app 下 account 与埋点对象。
  const { account, sensors } = state[namespace.app];
  // 解构埋点方法和事件映射。
  const { track, TRACK_EVENTS } = sensors || {};
  // 获取 SLS 上报方法。
  const { reportSlsData } = useSls();

  // 首次加载时预热点赞/收藏动效资源。
  useEffect(() => {
    // 预热两个动效资源。
    preloadDotLottieAssets([USER_ACTION_LOTTIE_URL.PRAISE, USER_ACTION_LOTTIE_URL.COLLECT]);
  }, []);

  // 计算登录态。
  const isLoggin = useMemo(() => {
    // 解构账号关键信息。
    const { id, uuid, status } = account || {};
    // 状态完成且有 id/uuid 视为已登录。
    if (status !== PromiseState.PENDING && id && uuid) {
      // 返回已登录。
      return true;
    }
    // 否则未登录。
    return false;
  }, [account]);

  // 包装 SLS 上报函数。
  const onReportSlsData = (data: Array<SlsData>) => {
    // 上报指标。
    reportSlsData(data)
  }
  // 标记是否已有数据。
  const hasData = !!(data && data.length > 0);

  // 点赞/收藏统一埋点回调。
  const handlerTrackCb = (
    // 互动类型。
    type: USER_ACTION_TYPE,
    // 当前是否已选中。
    isSelected: boolean,
    // 本次操作是否成功。
    isSuccess: boolean,
    // 失败原因。
    failReason?: string,
    // 当前帖子项。
    item?: IThreadLisItem
  ) => {
    // 无 item 直接返回。
    if (!item) return;

    // 初始化按钮名。
    let buttonName = '';
    // 点赞按钮文案。
    if (type === USER_ACTION_TYPE.PRAISE) {
      // 已点赞则是取消赞，否则是点赞。
      buttonName = isSelected ? 'Unlike' : 'Like';
    } else {
      // 收藏按钮文案映射。
      buttonName = isSelected ? 'Uncollected' : 'Collected';
    }

    // 根据类型选择埋点事件。
    const eventName =
      // 点赞使用点赞结果事件。
      type === USER_ACTION_TYPE.PRAISE
        ? TRACK_EVENTS?.[namespace.home].threadLikeResult.name
        // 收藏使用收藏结果事件。
        : TRACK_EVENTS?.[namespace.home].threadCollectResult.name;

    // 成功时同步更新父层数据。
    if (isSuccess && onItemUpdate) {
      // 传递反转后的状态。
      onItemUpdate(item, type, !isSelected);
    }

    // 构造埋点参数。
    const baseParams: Record<string, any> = {
      // 页面名。
      page_name: pageName,
      // 平台类型。
      platform_type: 'pc',
      // 位置标识。
      position: 'Feed',
      // 帖子标题。
      thread_name: item.subject,
      // 帖子 ID。
      thread_tid: item.tid,
      // 作者账号 ID。
      account_slug: item.author?.id,
      // 按钮名称。
      button_name: buttonName,
      // 是否成功。
      is_success: isSuccess,
      // 失败原因。
      fail_reason: failReason,
    };
    // 有 listName 时补充到埋点参数。
    if (listName) {
      // 写入列表名。
      baseParams.list_name = listName;
    }

    // 发送埋点事件。
    track?.(eventName, baseParams, namespace.home);
  };

  // 无数据且不在加载时渲染空态。
  if ((!data || data.length === 0) && !loading) {
    // 返回空态节点。
    return (
      // 空态容器。
      <div className={styles.empty}>
        {/* 空态表情 */}
        <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
        {/* 空态文案 */}
        <p>{t('user_center_post_list_empty')}</p>
      </div>
    );
  }

  // 统一处理封面 URL。
  const getCoverUrl = (cover: any) => {
    // 字符串类型直接返回。
    if (typeof cover === 'string') return cover;
    // 对象类型按优先级返回可用尺寸。
    return cover?.medium || cover?.large || cover?.extra_large || '';
  };

  // 处理卡片点击行为。
  const handleClick = (item: IThreadLisItem) => {
    // 草稿模式跳编辑页。
    if (isDraft) {
       // 构造编辑参数。
       const query = new URLSearchParams({
         // 编辑模块。
         mod: 'post',
         // 编辑动作。
         action: 'edit',
         // fid 固定值。
         fid: '0',
         // 帖子 ID。
         tid: String(item.tid),
         // 帖子 pid。
         pid: String(item.pid || 0),
         // 来源标识。
         source: 'draft'
       }).toString();
       // 打开编辑页。
       window.open(`${pagePathUrl.edit}?${query}`, "_blank");
    } else {
       // 构造进入详情埋点基础参数。
       const baseParams: Record<string, any> = {
         // 页面名。
         page_name: pageName,
         // 平台类型。
         platform_type: 'pc',
         // 位置标识。
         position: 'Feed',
         // 帖子标题。
         thread_name: item.subject,
         // 帖子 ID。
         thread_tid: item.tid,
         // 作者账号 ID。
         account_slug: item.author?.id,
       };
       // 有列表名时补充字段。
       if (listName) {
         // 写入 list_name。
         baseParams.list_name = listName;
       }
       // 存在 tid 时按成功路径上报埋点。
       if (item.tid) {
         // 发送成功进入详情埋点。
         track?.(TRACK_EVENTS?.[namespace.home]?.threadEntryClick?.name, {
           // 合并基础参数。
           ...baseParams,
           // 成功标识。
           is_success: true,
           // 失败原因为空串。
           fail_reason: '',
         }, namespace.home);
       } else {
         // 无 tid 时上报失败埋点。
         track?.(TRACK_EVENTS?.[namespace.home]?.threadEntryClick?.name, {
           // 合并基础参数。
           ...baseParams,
           // 失败标识。
           is_success: false,
          // 失败原因文案。
          fail_reason: t('user_center_post_list_missing_tid'),
         }, namespace.home);
         // 终止跳转。
         return;
       }
       // 打开详情页。
       window.open(`${pagePathUrl.detail}?tid=${item.tid}`, "_blank");
    }
  };

  // 阻止子区域点击事件冒泡。
  const handleStopPropagation = (e: React.MouseEvent) => {
    // 阻止冒泡。
    e.stopPropagation();
  };

  // 清洗并解码展示文本。
  const getDisplayText = (text: string) =>
    // 先清洗 HTML 再做实体解码。
    decodeHtmlEntities(sanitizeHtml(text || '', {
      // 移除所有标签。
      allowedTags: [],
      // 移除所有属性。
      allowedAttributes: {},
    }));

  // 渲染帖子列表。
  return (
    // 列表容器。
    <div className={styles.list}>
      {/* 遍历帖子项 */}
      {data.map(item => {
        // 判断是否有封面。
        const hasCover = item.covers && item.covers.length > 0;
        // 获取封面 URL。
        const coverUrl = hasCover ? getCoverUrl(item.covers![0]) : '';
        // 计算标题展示文本。
        const displaySubject = getDisplayText(item.subject || '');
        // 计算摘要展示文本。
        const displaySummary = getDisplayText(item.summary || '');

        // 返回单条帖子项。
        return (
          // 单条项容器。
          <div 
            // 使用 tid 作为 key。
            key={item.tid} 
            // 绑定样式类。
            className={styles.item} 
            // 整卡点击跳转。
            onClick={() => handleClick(item)}
          >
             {/* 上半部分 */}
             <div className={styles.upperBody}>
               {/* 文本内容区 */}
               <div className={styles.textContent}>
                 {/* 标题区 */}
                 <span className={styles.title}>
                   {/* 草稿标签 */}
                   {isDraft && <span className={styles.draftTag}>{t('user_works_card_draft_label')}</span>}
                   {/* 标题文本 */}
                   {displaySubject}
                 </span>
                 {/* 摘要文本 */}
                 <p className={styles.summary}>{displaySummary}</p>
               </div>
               {/* 有封面时渲染封面 */}
               {hasCover && (
                 // 封面容器。
                 <div className={styles.cover}>
                   {/* 封面图片 */}
                   <CommonImage 
                    // 图片地址。
                    src={coverUrl} 
                    // 图片类型。
                    type={ImageType.image} 
                    // 布局方式。
                    layout="fill" 
                    // 裁切模式。
                    cutMode="cover" 
                  />
                 </div>
               )}
             </div>

             {/* 底部操作区 */}
             <div className={styles.footer}>
               {/* 草稿与已发布分支渲染 */}
               {isDraft ? (
                 // 草稿操作区域。
                 <div className={styles.draftAction}>
                  {/* 草稿提示文案 */}
                  <span>{t('user_center_post_list_click_edit_publish')}</span>
                  {/* 编辑图标 */}
                  <GoToEditIcon className={styles.icon} />
                </div>
               ) : (
                 // 已发布操作区域。
                 <div className={styles.actionWrapper}>
                   {/* 浏览量 */}
                   <span className={styles.views}>
                     {/* 浏览数字与文案 */}
                     {formatDisplayNumber(item.views)} {t('user_center_post_list_views')}
                   </span>
                   {/* 用户操作区 */}
                   <div className={styles.userAction} onClick={handleStopPropagation}>
                      {/* 点赞组件 */}
                      <UserAction
                        // 间距类名。
                        className={styles.gap}
                        // 帖子 ID。
                        tid={item.tid}
                        // 标题文案。
                        title={displaySubject}
                        // 操作类型：点赞。
                        type={USER_ACTION_TYPE.PRAISE}
                        // 初始点赞数。
                        count={item.praise || 0}    
                        // 初始点赞状态。
                        status={item.praised}
                        // 登录态。
                        isLoggin={isLoggin}
                        // 埋点回调。
                        trackCb={(type, isSelected, isSuccess, failReason) => handlerTrackCb(type, isSelected, isSuccess, failReason, item)}
                        // SLS 上报回调。
                        onReportSlsData={onReportSlsData}
                      />
                      {/* 收藏组件 */}
                      <UserAction
                        // 间距类名。
                        className={styles.gap}
                        // 帖子 ID。
                        tid={item.tid}
                        // 标题文案。
                        title={displaySubject}
                        // 操作类型：收藏。
                        type={USER_ACTION_TYPE.COLLECT}
                        // 初始收藏数。
                        count={item.favtimes || 0}
                        // 初始收藏状态。
                        status={item.favorite}
                        // 登录态。
                        isLoggin={isLoggin}
                        // 埋点回调。
                        trackCb={(type, isSelected, isSuccess, failReason) => handlerTrackCb(type, isSelected, isSuccess, failReason, item)}
                        // SLS 上报回调。
                        onReportSlsData={onReportSlsData}
                      />
                      {/* 评论入口 */}
                      <div className={styles.comment} onClick={() => handleClick(item)}>
                        {/* 评论图标容器 */}
                        <div className={styles.commentIcon}>
                          {/* 评论图标 */}
                          <CommentSvg />
                        </div>
                        {/* 评论计数 */}
                        <UserActionCount className={styles.commentNum} count={item.replies} />
                      </div>
                   </div>
                 </div>
               )}
             </div>
          </div>
        );
      })}
      {/* 列表 loading 状态 */}
      {loading && (hasData ? (
        // 已有数据时展示底部 loading。
        <div className={styles.paginationLoading}>
          {/* loading 点 */}
          <span className={styles.loadingDot} />
          {/* loading 文案 */}
          <span>{t('user_center_loading')}</span>
        </div>
      ) : Array.from({ length: 5 }).map((_, index) => (
        // 首屏 loading 时渲染骨架项。
        <SkeletonPostItem key={`skeleton-${index}`} />
      )))}
    </div>
  )
}

// 导出组件。
export default UserPostList;
